import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PlusIcon,
  FolderIcon,
  FolderOpenIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  DocumentIcon,
  LockClosedIcon,
  XMarkIcon,
  ArrowLeftIcon,
  EllipsisVerticalIcon,
  SparklesIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';

// ── Types ─────────────────────────────────────────────────────────────────────

interface FormQuestion {
  id: string;
  text: string;
  type: string;
  order_index: number;
  options?: string[] | null;
}

interface FormTemplate {
  id: string;
  title: string;
  description?: string;
  created_by: string | null;
  is_admin_template: boolean;
  folder_id: string | null;
  in_use: boolean;
  created_at: string;
  questions?: FormQuestion[];
}

interface FormFolder {
  id: string;
  name: string;
  coach_id: string;
  created_at: string;
}

interface Props {
  isAdmin?: boolean;
  adminCoaches?: { id: string; first_name: string; last_name: string; email: string }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const QUESTION_TYPE_LABELS: Record<string, string> = {
  rating: 'Rating (1–5)',
  yesno: 'Yes / No',
  freetext: 'Free Text',
  number: 'Number',
  dropdown_single: 'Dropdown (single)',
  dropdown_multi: 'Dropdown (multi-select)',
};

const QUESTION_TYPES = Object.keys(QUESTION_TYPE_LABELS);

function authHeader() {
  return {
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
    'Content-Type': 'application/json',
  };
}

// ── Template Builder ──────────────────────────────────────────────────────────

interface QuestionDraft {
  id: string;
  text: string;
  type: string;
  order_index: number;
  options: string[];
}

const TemplateBuilder: React.FC<{
  initial?: FormTemplate;
  folders: FormFolder[];
  defaultFolderId?: string | null;
  isAdmin?: boolean;
  onSave: (t: FormTemplate) => void;
  onCancel: () => void;
}> = ({ initial, folders, defaultFolderId, isAdmin, onSave, onCancel }) => {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [folderId, setFolderId] = useState<string>(initial?.folder_id ?? defaultFolderId ?? '');
  const [questions, setQuestions] = useState<QuestionDraft[]>(
    initial?.questions?.map((q, i) => ({
      id: q.id,
      text: q.text,
      type: q.type,
      order_index: i,
      options: q.options ?? [],
    })) ?? [],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function addQuestion() {
    setQuestions(prev => [
      ...prev,
      { id: crypto.randomUUID(), text: '', type: 'freetext', order_index: prev.length, options: [] },
    ]);
  }

  function removeQuestion(id: string) {
    setQuestions(prev => prev.filter(q => q.id !== id).map((q, i) => ({ ...q, order_index: i })));
  }

  function updateQuestion(id: string, patch: Partial<QuestionDraft>) {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, ...patch } : q));
  }

  function addOption(qId: string) {
    setQuestions(prev => prev.map(q =>
      q.id === qId ? { ...q, options: [...q.options, ''] } : q
    ));
  }

  function updateOption(qId: string, idx: number, val: string) {
    setQuestions(prev => prev.map(q =>
      q.id === qId
        ? { ...q, options: q.options.map((o, i) => i === idx ? val : o) }
        : q
    ));
  }

  function removeOption(qId: string, idx: number) {
    setQuestions(prev => prev.map(q =>
      q.id === qId ? { ...q, options: q.options.filter((_, i) => i !== idx) } : q
    ));
  }

  async function handleSave() {
    if (!title.trim()) { setError('Title is required'); return; }
    if (questions.length === 0) { setError('Add at least one question'); return; }
    for (const q of questions) {
      if (!q.text.trim()) { setError('All questions must have text'); return; }
      if (q.type.startsWith('dropdown') && q.options.filter(o => o.trim()).length < 2) {
        setError('Dropdown questions need at least 2 options'); return;
      }
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        folder_id: folderId || undefined,
        questions: questions.map((q, i) => ({
          text: q.text.trim(),
          type: q.type,
          order_index: i,
          options: q.type.startsWith('dropdown') ? q.options.filter(o => o.trim()) : undefined,
        })),
      };
      const url = initial
        ? `${config.apiBaseUrl}/forms/templates/${initial.id}`
        : `${config.apiBaseUrl}/forms/templates`;
      const method = initial ? 'PATCH' : 'POST';
      const r = await fetch(url, { method, headers: authHeader(), body: JSON.stringify(payload) });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError((d as any).detail || 'Save failed');
        return;
      }
      const saved = await r.json();
      onSave(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">Form Title</label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Weekly Check-In"
          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">Description (optional)</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
          placeholder="Brief description of what this form is for"
          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
        />
      </div>

      {!isAdmin && folders.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Folder</label>
          <select
            value={folderId}
            onChange={e => setFolderId(e.target.value)}
            className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Unfiled</option>
            {folders.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-400">Questions</span>
          <button
            type="button"
            onClick={addQuestion}
            className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300"
          >
            <PlusIcon className="w-3.5 h-3.5" /> Add Question
          </button>
        </div>
        <div className="space-y-3">
          {questions.map((q, idx) => (
            <div key={q.id} className="bg-dark-canvas border border-dark-border rounded-lg p-3 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-500 mt-2 w-4 flex-shrink-0">{idx + 1}.</span>
                <div className="flex-1 space-y-2">
                  <input
                    value={q.text}
                    onChange={e => updateQuestion(q.id, { text: e.target.value })}
                    placeholder="Question text"
                    className="w-full bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <select
                    value={q.type}
                    onChange={e => updateQuestion(q.id, { type: e.target.value, options: [] })}
                    className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {QUESTION_TYPES.map(t => (
                      <option key={t} value={t}>{QUESTION_TYPE_LABELS[t]}</option>
                    ))}
                  </select>

                  {q.type.startsWith('dropdown') && (
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Options:</p>
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-1">
                          <input
                            value={opt}
                            onChange={e => updateOption(q.id, oi, e.target.value)}
                            placeholder={`Option ${oi + 1}`}
                            className="flex-1 bg-dark-hover border border-dark-border rounded-lg px-2 py-1 text-xs text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                          <button
                            type="button"
                            onClick={() => removeOption(q.id, oi)}
                            className="text-gray-500 hover:text-red-400"
                          >
                            <XMarkIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addOption(q.id)}
                        className="text-xs text-primary-400 hover:text-primary-300"
                      >
                        + Add option
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeQuestion(q.id)}
                  className="text-gray-500 hover:text-red-400 mt-1.5 flex-shrink-0"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {questions.length === 0 && (
            <button
              type="button"
              onClick={addQuestion}
              className="w-full border-2 border-dashed border-dark-border rounded-lg py-6 text-sm text-gray-500 hover:border-primary-600 hover:text-primary-400 transition-colors"
            >
              + Add your first question
            </button>
          )}
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg px-3 py-2">{error}</p>
      )}

      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
        >
          {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Create Form')}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 border border-dark-border rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Assign Modal ──────────────────────────────────────────────────────────────

interface ClientOption {
  relationship_id: string;
  client_id: string;
  client_name: string;
  client_email: string;
}

function getCoachIdFromToken(): string | null {
  try {
    const token = localStorage.getItem('authToken') || '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.user_id || null;
  } catch {
    return null;
  }
}

const FREQ_OPTIONS = [
  { value: 'once',     label: 'One-off' },
  { value: 'weekly',   label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
] as const;

type FreqValue = 'once' | 'weekly' | 'biweekly';

const AssignModal: React.FC<{
  template: FormTemplate;
  onClose: () => void;
  onAssigned: () => void;
}> = ({ template, onClose, onAssigned }) => {
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [relationshipId, setRelationshipId] = useState('');
  const [frequency, setFrequency] = useState<FreqValue>('once');
  const [maxOccurrences, setMaxOccurrences] = useState(12);
  const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    async function loadClients() {
      const coachId = getCoachIdFromToken();
      if (!coachId) { setLoadingClients(false); return; }
      try {
        const r = await fetch(`${config.apiBaseUrl}/relationships/coach/${coachId}/clients`, {
          headers: authHeader(),
        });
        if (r.ok) {
          const d = await r.json();
          setClients(d.clients ?? []);
        }
      } finally {
        setLoadingClients(false);
      }
    }
    loadClients();
  }, []);

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!relationshipId) { setError('Please select a client'); return; }
    setAssigning(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        coach_client_id: parseInt(relationshipId, 10),
        template_id: template.id,
        frequency,
        due_date: dueDate || undefined,
      };
      if (frequency !== 'once') {
        payload.max_occurrences = maxOccurrences;
      }
      const r = await fetch(`${config.apiBaseUrl}/questionnaires/assign`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError((d as any).detail || 'Assignment failed');
        return;
      }
      const clientName = clients.find(c => c.relationship_id === relationshipId)?.client_name ?? 'client';
      setSuccess(`Assigned to ${clientName}. They'll receive an email notification.`);
      setTimeout(() => { onAssigned(); onClose(); }, 1800);
    } finally {
      setAssigning(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-dark-card border border-dark-border rounded-xl shadow-xl p-6 w-96"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-white mb-1">Assign to Client</h3>
        <p className="text-xs text-gray-400 mb-5 truncate">{template.title}</p>

        {success ? (
          <div className="py-4 text-center">
            <p className="text-sm text-green-400">{success}</p>
          </div>
        ) : (
          <form onSubmit={handleAssign} className="space-y-4">
            {/* Client */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Client</label>
              {loadingClients ? (
                <div className="flex items-center gap-2 py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-400" />
                  <span className="text-xs text-gray-500">Loading clients…</span>
                </div>
              ) : clients.length === 0 ? (
                <p className="text-xs text-gray-500 py-2">No active clients found.</p>
              ) : (
                <select
                  value={relationshipId}
                  onChange={e => setRelationshipId(e.target.value)}
                  required
                  className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select a client…</option>
                  {clients.map(c => (
                    <option key={c.relationship_id} value={c.relationship_id}>
                      {c.client_name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Schedule */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-2">Schedule</label>
              <div className="flex gap-2">
                {FREQ_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFrequency(value)}
                    className={`flex-1 px-2 py-2 rounded-lg border text-xs font-medium transition-colors ${
                      frequency === value
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-dark-canvas text-gray-300 border-dark-border hover:border-gray-500'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Max occurrences — recurring only */}
            {frequency !== 'once' && (
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Max responses
                </label>
                <select
                  value={maxOccurrences}
                  onChange={e => setMaxOccurrences(Number(e.target.value))}
                  className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? 'response' : 'responses'}</option>
                  ))}
                </select>
              </div>
            )}

            {/* First due date */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">First due date</label>
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {error && <p className="text-xs text-red-400">{error}</p>}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm text-gray-400 hover:text-gray-200 border border-dark-border rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={assigning || loadingClients || clients.length === 0}
                className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {assigning ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ── Unassign Modal ────────────────────────────────────────────────────────────

interface Assignment {
  questionnaire_id: string;
  coach_client_id: number;
  client_name: string;
  frequency: string;
  next_due_date: string | null;
}

const UnassignModal: React.FC<{
  template: FormTemplate;
  onClose: () => void;
  onUnassigned: () => void;
}> = ({ template, onClose, onUnassigned }) => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [unassigning, setUnassigning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetch(`${config.apiBaseUrl}/forms/templates/${template.id}/assignments`, {
      headers: authHeader(),
    })
      .then(r => r.json())
      .then(d => setAssignments(d.assignments ?? []))
      .catch(() => setError('Failed to load assignments'))
      .finally(() => setLoading(false));
  }, [template.id]);

  async function handleUnassign() {
    if (!selectedId) return;
    setUnassigning(true);
    setError('');
    try {
      const r = await fetch(`${config.apiBaseUrl}/questionnaires/${selectedId}/unassign`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError((d as any).detail || 'Unassign failed');
        return;
      }
      const name = assignments.find(a => a.questionnaire_id === selectedId)?.client_name ?? 'client';
      setSuccess(`Form unassigned from ${name}.`);
      setTimeout(() => { onUnassigned(); onClose(); }, 1500);
    } finally {
      setUnassigning(false);
    }
  }

  const freqLabel: Record<string, string> = { once: 'One-off', weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-dark-card border border-dark-border rounded-xl shadow-xl p-6 w-96"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-white mb-1">Unassign Form</h3>
        <p className="text-xs text-gray-400 mb-5 truncate">{template.title}</p>

        {success ? (
          <div className="py-4 text-center">
            <p className="text-sm text-green-400">{success}</p>
          </div>
        ) : loading ? (
          <div className="flex items-center gap-2 py-4">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-400" />
            <span className="text-xs text-gray-500">Loading assignments…</span>
          </div>
        ) : assignments.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No active assignments for this form.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-2">Select client to unassign</label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {assignments.map(a => (
                  <label
                    key={a.questionnaire_id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedId === a.questionnaire_id
                        ? 'border-red-500/50 bg-red-900/10'
                        : 'border-dark-border hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="assignment"
                      value={a.questionnaire_id}
                      checked={selectedId === a.questionnaire_id}
                      onChange={() => setSelectedId(a.questionnaire_id)}
                      className="mt-0.5 accent-red-500"
                    />
                    <div>
                      <p className="text-sm text-gray-200">{a.client_name}</p>
                      <p className="text-xs text-gray-500">
                        {freqLabel[a.frequency] ?? a.frequency}
                        {a.next_due_date ? ` · next due ${a.next_due_date}` : ''}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {error && <p className="text-xs text-red-400">{error}</p>}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm text-gray-400 hover:text-gray-200 border border-dark-border rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUnassign}
                disabled={!selectedId || unassigning}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {unassigning ? 'Unassigning…' : 'Unassign'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Template Preview Modal ────────────────────────────────────────────────────

const TemplatePreviewModal: React.FC<{ template: FormTemplate; onClose: () => void }> = ({ template: t, onClose }) => {
  const [questions, setQuestions] = useState<FormQuestion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${config.apiBaseUrl}/forms/templates/${t.id}`, { headers: authHeader() })
      .then(r => r.json())
      .then(d => setQuestions(d.questions ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [t.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-dark-card border border-dark-border rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-dark-border flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-white truncate">{t.title}</h3>
            {t.description && <p className="text-xs text-gray-400 mt-0.5">{t.description}</p>}
          </div>
          <button onClick={onClose} className="ml-3 flex-shrink-0 text-gray-500 hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading && <p className="text-xs text-gray-400 text-center py-4">Loading…</p>}
          {!loading && questions.length === 0 && (
            <p className="text-xs text-gray-500 text-center py-4">No questions in this form.</p>
          )}
          {questions.map((q, idx) => (
            <div key={q.id} className="bg-dark-canvas border border-dark-border rounded-xl p-4">
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-600 mt-0.5 w-5 flex-shrink-0">{idx + 1}.</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-200">{q.text}</p>
                  <span className="inline-block mt-1 text-xs text-gray-500 bg-dark-sidebar border border-dark-border rounded-full px-2 py-0.5">
                    {QUESTION_TYPE_LABELS[q.type] ?? q.type}
                  </span>
                  {q.options && q.options.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {q.options.map((opt, oi) => (
                        <span key={oi} className="text-xs bg-dark-hover border border-dark-border rounded-full px-2 py-0.5 text-gray-400">
                          {opt}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Template Card ─────────────────────────────────────────────────────────────

const TemplateCard: React.FC<{
  template: FormTemplate;
  folders: FormFolder[];
  isAdmin?: boolean;
  onEdit: (t: FormTemplate) => void;
  onDelete: (id: string) => void;
  onCopy: (id: string) => void;
  onMove: (id: string, folderId: string | null) => void;
  onAssign?: (t: FormTemplate) => void;
  onUnassign?: (t: FormTemplate) => void;
  onView: (t: FormTemplate) => void;
}> = ({ template: t, folders, isAdmin, onEdit, onDelete, onCopy, onMove, onAssign, onUnassign, onView }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div className="flex items-start justify-between bg-dark-canvas border border-dark-border rounded-xl p-3 group hover:border-gray-600 transition-colors">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-200 truncate">{t.title}</p>
          {t.in_use && (
            <span title="In use — cannot edit or delete">
              <LockClosedIcon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
            </span>
          )}
          {t.is_admin_template && (
            <span className="flex items-center gap-0.5 text-xs text-violet-400 bg-violet-900/30 border border-violet-800/40 rounded-full px-1.5 py-0.5 flex-shrink-0">
              <SparklesIcon className="w-3 h-3" /> System
            </span>
          )}
        </div>
        {t.description && (
          <p className="text-xs text-gray-500 mt-0.5 truncate">{t.description}</p>
        )}
        {t.questions && (
          <p className="text-xs text-gray-600 mt-0.5">{t.questions.length} question{t.questions.length !== 1 ? 's' : ''}</p>
        )}
      </div>

      <div className="flex items-center gap-1 ml-3 flex-shrink-0" ref={menuRef}>
        {/* Menu for own / admin templates */}
        {(!t.is_admin_template || isAdmin) && (
          <div className="relative">
            <button
              onClick={() => setMenuOpen(v => !v)}
              className="p-1.5 text-gray-500 hover:text-gray-300 rounded transition-colors"
            >
              <EllipsisVerticalIcon className="w-4 h-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-8 z-30 w-44 bg-dark-card border border-dark-border rounded-xl shadow-xl py-1">
                <button
                  onClick={() => { setMenuOpen(false); onView(t); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-dark-hover"
                >
                  <EyeIcon className="w-3.5 h-3.5" /> View Form
                </button>
                {!t.is_admin_template && onAssign && (
                  <button
                    onClick={() => { setMenuOpen(false); onAssign(t); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-dark-hover"
                  >
                    <DocumentIcon className="w-3.5 h-3.5" /> Assign to Client
                  </button>
                )}
                {!t.is_admin_template && onUnassign && t.in_use && (
                  <button
                    onClick={() => { setMenuOpen(false); onUnassign(t); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-dark-hover"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" /> Unassign
                  </button>
                )}
                {!t.in_use && (
                  <button
                    onClick={() => { setMenuOpen(false); onEdit(t); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-dark-hover"
                  >
                    <PencilIcon className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
                <button
                  onClick={() => { setMenuOpen(false); onCopy(t.id); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-dark-hover"
                >
                  <DocumentDuplicateIcon className="w-3.5 h-3.5" /> Duplicate
                </button>
                {!t.is_admin_template && folders.length > 0 && (
                  <button
                    onClick={() => { setMenuOpen(false); setShowMove(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-dark-hover"
                  >
                    <FolderIcon className="w-3.5 h-3.5" /> Move to folder
                  </button>
                )}
                {!t.in_use && (
                  <>
                    <div className="my-1 border-t border-dark-border" />
                    <button
                      onClick={() => { setMenuOpen(false); onDelete(t.id); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-900/20"
                    >
                      <TrashIcon className="w-3.5 h-3.5" /> Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* View + Copy buttons for system templates (coach view only) */}
        {t.is_admin_template && !isAdmin && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onView(t)}
              title="Preview form"
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 px-2 py-1 rounded transition-colors"
            >
              <EyeIcon className="w-3.5 h-3.5" /> View
            </button>
            <button
              onClick={() => onCopy(t.id)}
              title="Copy to My Forms"
              className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300 px-2 py-1 rounded transition-colors"
            >
              <DocumentDuplicateIcon className="w-3.5 h-3.5" /> Copy
            </button>
          </div>
        )}
      </div>

      {/* Move to folder modal */}
      {showMove && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setShowMove(false)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-xl shadow-xl p-5 w-72"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-white mb-3">Move to folder</h3>
            <div className="space-y-1">
              <button
                onClick={() => { setShowMove(false); onMove(t.id, null); }}
                className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${!t.folder_id ? 'bg-primary-900/40 text-primary-300' : 'text-gray-300 hover:bg-dark-hover'}`}
              >
                Unfiled
              </button>
              {folders.map(f => (
                <button
                  key={f.id}
                  onClick={() => { setShowMove(false); onMove(t.id, f.id); }}
                  className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${t.folder_id === f.id ? 'bg-primary-900/40 text-primary-300' : 'text-gray-300 hover:bg-dark-hover'}`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── FormsLibrarySection ───────────────────────────────────────────────────────

type ViewMode = 'library' | 'create' | 'edit';
// 'all' = all visible templates, 'system' = CoachSight Library, null = Unfiled, string = folder ID
type FolderKey = 'all' | 'system' | string | null;

const FormsLibrarySection: React.FC<Props> = ({ isAdmin = false, adminCoaches = [] }) => {
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [folders, setFolders] = useState<FormFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewMode>('library');
  const [editingTemplate, setEditingTemplate] = useState<FormTemplate | null>(null);
  const [defaultFolderId, setDefaultFolderId] = useState<string | null>(null);
  const [error, setError] = useState('');

  // Folder selection
  const [selectedFolderKey, setSelectedFolderKey] = useState<FolderKey>(isAdmin ? 'system' : 'all');

  // Assign modal
  const [assigningTemplate, setAssigningTemplate] = useState<FormTemplate | null>(null);

  // Unassign modal
  const [unassigningTemplate, setUnassigningTemplate] = useState<FormTemplate | null>(null);

  // View (preview) modal
  const [viewingTemplate, setViewingTemplate] = useState<FormTemplate | null>(null);

  // Admin coach filter
  const [filterCoachId, setFilterCoachId] = useState('');

  // Inline new folder
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [folderSaving, setFolderSaving] = useState(false);
  const [folderError, setFolderError] = useState('');

  // Rename folder modal
  const [renamingFolder, setRenamingFolder] = useState<FormFolder | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Delete folder confirm
  const [deletingFolder, setDeletingFolder] = useState<FormFolder | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = isAdmin && filterCoachId ? `?coach_id=${filterCoachId}` : '';
      const r = await fetch(`${config.apiBaseUrl}/forms/templates${params}`, {
        headers: authHeader(),
      });
      if (r.ok) {
        const d = await r.json();
        setTemplates(d.templates ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [isAdmin, filterCoachId]);

  const loadFolders = useCallback(async () => {
    if (isAdmin) return;
    const r = await fetch(`${config.apiBaseUrl}/forms/folders`, { headers: authHeader() });
    if (r.ok) {
      const d = await r.json();
      setFolders(d.folders ?? []);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadTemplates();
    loadFolders();
  }, [loadTemplates, loadFolders]);

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this form template?')) return;
    const r = await fetch(`${config.apiBaseUrl}/forms/templates/${id}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (r.ok) {
      setTemplates(prev => prev.filter(t => t.id !== id));
    } else {
      const e = await r.json().catch(() => ({}));
      setError((e as any).detail || 'Could not delete');
    }
  }

  async function handleCopy(id: string) {
    const r = await fetch(`${config.apiBaseUrl}/forms/templates/${id}/copy`, {
      method: 'POST',
      headers: authHeader(),
    });
    if (r.ok) {
      const copy = await r.json();
      setTemplates(prev => [copy, ...prev]);
    } else {
      setError('Could not copy template');
    }
  }

  async function handleMove(templateId: string, folderId: string | null) {
    const r = await fetch(`${config.apiBaseUrl}/forms/templates/${templateId}`, {
      method: 'PATCH',
      headers: authHeader(),
      body: JSON.stringify({ folder_id: folderId ?? '' }),
    });
    if (r.ok) {
      const updated = await r.json();
      setTemplates(prev => prev.map(t => t.id === templateId ? { ...t, folder_id: updated.folder_id } : t));
    }
  }

  async function handleEditClick(t: FormTemplate) {
    const r = await fetch(`${config.apiBaseUrl}/forms/templates/${t.id}`, { headers: authHeader() });
    if (r.ok) {
      const full = await r.json();
      setEditingTemplate(full);
      setView('edit');
    }
  }

  function handleSaved(saved: FormTemplate) {
    if (view === 'edit') {
      setTemplates(prev => prev.map(t => t.id === saved.id ? saved : t));
    } else {
      setTemplates(prev => [saved, ...prev]);
    }
    setView('library');
    setEditingTemplate(null);
  }

  // ── Folder management ─────────────────────────────────────────────────────

  async function handleCreateFolder(e: React.FormEvent) {
    e.preventDefault();
    const name = newFolderName.trim();
    if (!name) { setFolderError('Name required'); return; }
    setFolderSaving(true);
    setFolderError('');
    try {
      const r = await fetch(`${config.apiBaseUrl}/forms/folders`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ name }),
      });
      if (r.ok) {
        const f = await r.json();
        setFolders(prev => [...prev, f]);
        setShowNewFolderInput(false);
        setNewFolderName('');
      } else {
        const e = await r.json().catch(() => ({}));
        setFolderError((e as any).detail || 'Error creating folder');
      }
    } finally {
      setFolderSaving(false);
    }
  }

  async function handleRenameFolder() {
    if (!renamingFolder) return;
    const name = renameValue.trim();
    if (!name) return;
    const r = await fetch(`${config.apiBaseUrl}/forms/folders/${renamingFolder.id}`, {
      method: 'PATCH',
      headers: authHeader(),
      body: JSON.stringify({ name }),
    });
    if (r.ok) {
      const updated = await r.json();
      setFolders(prev => prev.map(f => f.id === updated.id ? updated : f));
    }
    setRenamingFolder(null);
  }

  async function handleDeleteFolder(folder: FormFolder) {
    const r = await fetch(`${config.apiBaseUrl}/forms/folders/${folder.id}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (r.ok) {
      setFolders(prev => prev.filter(f => f.id !== folder.id));
      setTemplates(prev => prev.map(t => t.folder_id === folder.id ? { ...t, folder_id: null } : t));
      if (selectedFolderKey === folder.id) setSelectedFolderKey('all');
    }
    setDeletingFolder(null);
  }

  // ── Derived data ──────────────────────────────────────────────────────────

  const systemTemplates = useMemo(() => templates.filter(t => t.is_admin_template), [templates]);
  const coachTemplates = useMemo(() => templates.filter(t => !t.is_admin_template), [templates]);
  const templatesByFolder = useCallback(
    (fid: string | null) => coachTemplates.filter(t => t.folder_id === fid),
    [coachTemplates],
  );
  const unfiledTemplates = useMemo(() => templatesByFolder(null), [templatesByFolder]);

  const visibleTemplates = useMemo(() => {
    if (selectedFolderKey === 'all') return templates;
    if (selectedFolderKey === 'system') return systemTemplates;
    if (selectedFolderKey === null) return unfiledTemplates;
    return coachTemplates.filter(t => t.folder_id === selectedFolderKey);
  }, [templates, systemTemplates, coachTemplates, unfiledTemplates, selectedFolderKey]);

  const selectedFolderLabel = useMemo(() => {
    if (selectedFolderKey === 'all') return 'All Forms';
    if (selectedFolderKey === 'system') return 'CoachSight Library';
    if (selectedFolderKey === null) return 'Unfiled';
    return folders.find(f => f.id === selectedFolderKey)?.name ?? 'Folder';
  }, [selectedFolderKey, folders]);

  // defaultFolderId for new form: use the selected folder if it's a real folder ID
  function getCreateDefaultFolderId(): string | null {
    if (selectedFolderKey === 'all' || selectedFolderKey === 'system' || selectedFolderKey === null) return null;
    return selectedFolderKey;
  }

  // ── Builder view ──────────────────────────────────────────────────────────

  if (view === 'create' || view === 'edit') {
    return (
      <div className="p-8">
        <button
          onClick={() => { setView('library'); setEditingTemplate(null); }}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200 mb-6 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to Forms Library
        </button>
        <h1 className="text-2xl font-bold text-white mb-6">
          {view === 'create' ? 'New Form' : 'Edit Form'}
        </h1>
        <TemplateBuilder
          initial={view === 'edit' ? editingTemplate ?? undefined : undefined}
          folders={folders}
          defaultFolderId={defaultFolderId}
          isAdmin={isAdmin}
          onSave={handleSaved}
          onCancel={() => { setView('library'); setEditingTemplate(null); }}
        />
      </div>
    );
  }

  // ── Library view — split panel ────────────────────────────────────────────

  return (
    <div className="p-8">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Forms Library</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          {isAdmin
            ? 'Manage system forms available to all coaches and browse coach-created forms.'
            : 'Organize reusable forms for your clients.'
          }
        </p>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-3 flex items-center gap-2">
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-300 hover:text-red-200">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-400" />
        </div>
      ) : (
        <div className="flex gap-4">

          {/* ── Left panel: folder sidebar ── */}
          <div className="w-48 flex-shrink-0">
            <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">

              {/* All Forms — top level (coach only) */}
              {!isAdmin && (
                <button
                  onClick={() => setSelectedFolderKey('all')}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-dark-hover transition-colors ${
                    selectedFolderKey === 'all'
                      ? 'bg-primary-900/30 text-primary-400 font-medium'
                      : 'text-gray-300'
                  }`}
                >
                  <FolderOpenIcon className="w-4 h-4 flex-shrink-0" />
                  All Forms
                  <span className="ml-auto text-xs text-gray-500">{templates.length}</span>
                </button>
              )}

              {/* CoachSight Library */}
              <button
                onClick={() => setSelectedFolderKey('system')}
                className={`w-full flex items-center gap-2 text-sm text-left hover:bg-dark-hover border-dark-border transition-colors ${
                  isAdmin ? 'px-3 py-2.5' : 'pl-7 pr-3 py-2 border-t'
                } ${
                  selectedFolderKey === 'system'
                    ? 'bg-primary-900/30 text-primary-400 font-medium'
                    : 'text-gray-500'
                }`}
              >
                <SparklesIcon className={`flex-shrink-0 ${isAdmin ? 'w-4 h-4' : 'w-3.5 h-3.5'}`} />
                <span className="truncate">CoachSight Library</span>
                <span className="ml-auto text-xs text-gray-600 flex-shrink-0">{systemTemplates.length}</span>
              </button>

              {/* Coach custom folders */}
              {!isAdmin && folders.map(f => (
                <div key={f.id} className="group relative border-t border-dark-border">
                  <button
                    onClick={() => setSelectedFolderKey(f.id)}
                    className={`w-full flex items-center gap-2 pl-7 pr-3 py-2 text-sm text-left hover:bg-dark-hover transition-colors ${
                      selectedFolderKey === f.id
                        ? 'bg-primary-900/30 text-primary-400 font-medium'
                        : 'text-gray-500'
                    }`}
                  >
                    <FolderIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate flex-1 text-left">{f.name}</span>
                    <span className="text-xs text-gray-600 flex-shrink-0">{templatesByFolder(f.id).length}</span>
                  </button>
                  {/* Hover actions — rename / delete */}
                  <div className="absolute right-1 top-1.5 hidden group-hover:flex items-center gap-0.5 bg-dark-card rounded">
                    <button
                      onClick={e => { e.stopPropagation(); setRenamingFolder(f); setRenameValue(f.name); }}
                      className="p-1 text-gray-500 hover:text-gray-300 rounded"
                      title="Rename"
                    >
                      <PencilIcon className="w-3 h-3" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setDeletingFolder(f); }}
                      className="p-1 text-gray-500 hover:text-red-400 rounded"
                      title="Delete folder"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Unfiled — coach only */}
              {!isAdmin && (
                <button
                  onClick={() => setSelectedFolderKey(null)}
                  className={`w-full flex items-center gap-2 pl-7 pr-3 py-2 text-sm text-left hover:bg-dark-hover border-t border-dark-border transition-colors ${
                    selectedFolderKey === null
                      ? 'bg-primary-900/30 text-primary-400 font-medium'
                      : 'text-gray-500'
                  }`}
                >
                  <DocumentIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  Unfiled
                  <span className="ml-auto text-xs text-gray-600">{unfiledTemplates.length}</span>
                </button>
              )}
            </div>

            {/* New Folder — inline input, coach only */}
            {!isAdmin && (
              showNewFolderInput ? (
                <form onSubmit={handleCreateFolder} className="mt-2 flex gap-1">
                  <input
                    autoFocus
                    value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    placeholder="Folder name"
                    className="flex-1 min-w-0 px-2 py-1.5 bg-dark-canvas border border-dark-border rounded-lg text-xs text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    type="submit"
                    disabled={folderSaving}
                    className="px-2 py-1.5 bg-primary-600 text-white text-xs rounded-lg disabled:opacity-50"
                  >
                    {folderSaving ? '…' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); setFolderError(''); }}
                    className="px-2 py-1.5 text-gray-500 hover:text-gray-300 text-xs"
                  >
                    ✕
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setShowNewFolderInput(true)}
                  className="mt-2 w-full flex items-center gap-1.5 px-3 py-2 text-xs text-gray-500 hover:text-primary-400 hover:bg-dark-hover rounded-xl border border-dashed border-dark-border transition-colors"
                >
                  <PlusIcon className="w-3.5 h-3.5" /> New Folder
                </button>
              )
            )}
            {folderError && <p className="mt-1 text-xs text-red-400">{folderError}</p>}
          </div>

          {/* ── Right panel: templates ── */}
          <div className="flex-1 min-w-0">
            {/* Right panel header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-100">{selectedFolderLabel}</h2>
              <div className="flex items-center gap-2">
                {/* Admin coach filter */}
                {isAdmin && (
                  <select
                    value={filterCoachId}
                    onChange={e => setFilterCoachId(e.target.value)}
                    className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All coaches</option>
                    {adminCoaches.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                      </option>
                    ))}
                  </select>
                )}
                {/* New Form — coaches always; admin only in CoachSight Library */}
                {(!isAdmin || selectedFolderKey === 'system') && (
                  <button
                    onClick={() => { setDefaultFolderId(getCreateDefaultFolderId()); setView('create'); }}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    New Form
                  </button>
                )}
              </div>
            </div>

            {/* Template list */}
            {visibleTemplates.length === 0 ? (
              <div className="text-center py-16">
                <FolderOpenIcon className="w-10 h-10 mx-auto mb-2 text-gray-700" />
                <p className="text-sm text-gray-500">No forms here yet.</p>
                {(!isAdmin || selectedFolderKey === 'system') && (
                  <button
                    onClick={() => { setDefaultFolderId(getCreateDefaultFolderId()); setView('create'); }}
                    className="mt-2 text-xs text-primary-400 hover:text-primary-300"
                  >
                    Create your first form
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {visibleTemplates.map(t => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    folders={folders}
                    isAdmin={isAdmin}
                    onEdit={handleEditClick}
                    onDelete={handleDelete}
                    onCopy={handleCopy}
                    onMove={handleMove}
                    onAssign={!isAdmin ? setAssigningTemplate : undefined}
                    onUnassign={!isAdmin ? setUnassigningTemplate : undefined}
                    onView={setViewingTemplate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview modal */}
      {viewingTemplate && (
        <TemplatePreviewModal
          template={viewingTemplate}
          onClose={() => setViewingTemplate(null)}
        />
      )}

      {/* Assign to client modal */}
      {assigningTemplate && (
        <AssignModal
          template={assigningTemplate}
          onClose={() => setAssigningTemplate(null)}
          onAssigned={loadTemplates}
        />
      )}

      {/* Unassign from client modal */}
      {unassigningTemplate && (
        <UnassignModal
          template={unassigningTemplate}
          onClose={() => setUnassigningTemplate(null)}
          onUnassigned={loadTemplates}
        />
      )}

      {/* Rename folder modal */}
      {renamingFolder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setRenamingFolder(null)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-xl shadow-xl p-6 w-80"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-white mb-4">Rename Folder</h3>
            <input
              autoFocus
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleRenameFolder(); }}
              className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-3"
            />
            <div className="flex gap-2">
              <button
                onClick={handleRenameFolder}
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white rounded-lg py-2 text-sm font-medium transition-colors"
              >
                Save
              </button>
              <button
                onClick={() => setRenamingFolder(null)}
                className="px-4 text-sm text-gray-400 hover:text-gray-200 border border-dark-border rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete folder confirm */}
      {deletingFolder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setDeletingFolder(null)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-xl shadow-xl p-6 w-80"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-white mb-2">Delete folder?</h3>
            <p className="text-xs text-gray-400 mb-4">
              Forms in "{deletingFolder.name}" will become unfiled. The forms themselves are not deleted.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleDeleteFolder(deletingFolder)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg py-2 text-sm font-medium transition-colors"
              >
                Delete Folder
              </button>
              <button
                onClick={() => setDeletingFolder(null)}
                className="px-4 text-sm text-gray-400 hover:text-gray-200 border border-dark-border rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsLibrarySection;
